import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Globe,
  Key,
  Link2,
  Loader2,
  Lock,
  Package,
  Pause,
  Pencil,
  Play,
  Power,
  RefreshCw,
  Server,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CopyId, StatusPill } from '@/components/page';
import {
  API_URL,
  AUTH_METHODS,
  Callout,
  CodeBlock,
  ConfirmDialog,
  DevLabel,
  EmptyState,
  ENDPOINTS,
  GrantPicker,
  Hint,
  InlineError,
  KV,
  Mark,
  Mono,
  PKCE_SNIPPET,
  ScopePicker,
  SCOPE_INFO,
  SecretDialog,
  SecretField,
  Tag,
  UriListEditor,
  ago,
  authorizeSnippet,
  fmtDate,
  fmtDateTime,
  tokenSnippet,
  validateOptionalHttps,
  validateUriList,
} from '@/components/oauth';
import { auditApi } from '@/api';
import {
  useClient,
  useDeleteClient,
  useRotateClientSecret,
  useToast,
  useUpdateClient,
} from '@/hooks';
import type { GrantType, OAuthClient, TokenEndpointAuthMethod } from '@/types';

const TABS = ['overview', 'redirects', 'credentials', 'logout', 'settings'] as const;
type TabKey = (typeof TABS)[number];

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { data: client, isLoading, isError, refetch } = useClient(id);
  const update = useUpdateClient();

  const hash = location.hash.replace('#', '') as TabKey;
  const tab: TabKey = TABS.includes(hash) ? hash : 'overview';
  const setTab = (t: string) => navigate({ hash: t === 'overview' ? '' : `#${t}` }, { replace: true });

  const [toggling, setToggling] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);

  useEffect(() => {
    if (client) document.title = `${client.name} · authrz.me`;
  }, [client]);

  const setActive = async (next: boolean) => {
    if (!client) return;
    setToggling(true);
    try {
      await update.mutateAsync({ id: client.id, data: { is_active: next } as never });
      toast({
        title: next ? 'Client enabled' : 'Client disabled',
        description: client.name,
      });
      setConfirmDisable(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: next ? 'Could not enable client' : 'Could not disable client',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    } finally {
      setToggling(false);
    }
  };

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="Loading client" className="animate-pulse space-y-5">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-foreground/[0.07]" />
          <div className="space-y-2">
            <div className="h-6 w-56 rounded bg-foreground/[0.07]" />
            <div className="h-4 w-80 rounded bg-foreground/[0.05]" />
          </div>
        </div>
        <div className="h-64 rounded-lg border border-border bg-card" />
      </div>
    );
  }

  if (isError || !client) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <EmptyState
          icon={<Key />}
          title={isError ? 'Couldn’t load this client' : 'Client not found'}
          action={
            <div className="flex gap-2">
              {isError && (
                <Button variant="outline" onClick={() => void refetch()}>
                  Try again
                </Button>
              )}
              <Button variant="outline" asChild>
                <Link to="/clients">All clients</Link>
              </Button>
            </div>
          }
        >
          This OAuth client doesn’t exist or isn’t in an organization you can access.
        </EmptyState>
      </div>
    );
  }

  const isPublic = client.client_type === 'public';
  const codeFlow = client.allowed_grant_types.includes('authorization_code');

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <Mark name={client.name} className="h-14 w-14 text-base" />
          <div className="min-w-0">
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
              <Link to="/clients" className="hover:text-foreground">
                OAuth clients
              </Link>{' '}
              / OAuth client
            </p>
            <h1 className="mt-1 truncate text-2xl font-semibold leading-tight tracking-[-0.03em]">
              {client.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5" />
                <Link
                  to={`/products/${client.product_id}`}
                  className="text-foreground hover:underline hover:underline-offset-4"
                >
                  {client.product_name || 'Unknown app'}
                </Link>
              </span>
              <span className="inline-flex items-center gap-1.5">
                {isPublic ? <Globe className="h-3.5 w-3.5" /> : <Server className="h-3.5 w-3.5" />}
                {isPublic ? 'Public client' : 'Confidential client'}
              </span>
              <StatusPill tone={client.is_active ? 'ok' : 'neutral'}>
                {client.is_active ? 'Active' : 'Disabled'}
              </StatusPill>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                Created {fmtDate(client.created_at)}
              </span>
            </div>
            <div className="mt-2.5">
              <CopyId value={client.client_id} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isPublic && codeFlow && client.is_active && (
            <Button variant="outline" asChild>
              <Link to={`/playground?client=${client.id}`}>
                <Play className="mr-2 h-4 w-4" />
                Test in playground
              </Link>
            </Button>
          )}
          {!isPublic && (
            <Button variant="outline" onClick={() => setTab('credentials')}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Rotate secret
            </Button>
          )}
        </div>
      </div>

      {!client.is_active && (
        <Callout
          tone="warn"
          title="This client is disabled"
          className="mb-5"
          action={
            <Button size="sm" variant="outline" disabled={toggling} onClick={() => void setActive(true)}>
              {toggling && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Enable client
            </Button>
          }
        >
          Sign-ins and token requests using this client ID are rejected.
        </Callout>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex w-full overflow-x-auto" aria-label="Client sections">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="redirects">
            Redirect URIs
            <span className="font-mono text-[11px] text-muted-foreground">{client.redirect_uris.length}</span>
          </TabsTrigger>
          <TabsTrigger value="credentials">Credentials</TabsTrigger>
          <TabsTrigger value="logout">Logout</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewPanel client={client} goto={setTab} />
        </TabsContent>
        <TabsContent value="redirects">
          <RedirectsPanel key={client.updated_at + client.redirect_uris.join('|')} client={client} />
        </TabsContent>
        <TabsContent value="credentials">
          <CredentialsPanel client={client} />
        </TabsContent>
        <TabsContent value="logout">
          <LogoutPanel key={client.updated_at} client={client} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsPanel
            key={client.updated_at}
            client={client}
            toggling={toggling}
            onToggle={() => (client.is_active ? setConfirmDisable(true) : void setActive(true))}
            onDeleted={(name) => {
              toast({ title: 'Client deleted', description: `${name} was removed and its tokens revoked.` });
              navigate('/clients');
            }}
          />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmDisable}
        onOpenChange={setConfirmDisable}
        danger
        title={`Disable ${client.name}?`}
        body="Sign-ins through this client fail immediately and its refresh tokens are rejected. You can enable it again later."
        confirmLabel="Disable client"
        pendingLabel="Disabling…"
        pending={toggling}
        onConfirm={() => setActive(false)}
      />
    </div>
  );
}

/* ── shared card chrome ─────────────────────────────────── */

function Panel({
  title,
  sub,
  action,
  children,
  footer,
  className,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`min-w-0 overflow-hidden rounded-lg border border-border bg-card ${className ?? ''}`}>
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-[18px]">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
          {sub && <p className="mt-0.5 text-[13px] text-muted-foreground">{sub}</p>}
        </div>
        {action}
      </div>
      <div className="space-y-4 p-4 sm:p-[18px]">{children}</div>
      {footer && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-4 py-3 sm:px-[18px]">
          {footer}
        </div>
      )}
    </section>
  );
}

/* ── overview ───────────────────────────────────────────── */

function OverviewPanel({ client: c, goto }: { client: OAuthClient; goto: (t: string) => void }) {
  const isPublic = c.client_type === 'public';
  const codeFlow = c.allowed_grant_types.includes('authorization_code');
  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ['audit', 'oauth_client', c.id],
    queryFn: () => auditApi.list({ resource: 'oauth_client', resourceId: c.id, limit: 6 }),
    retry: false,
  });

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="min-w-0 space-y-5">
        <Panel
          title="Configuration"
          sub="How this client authenticates and what it may request."
          action={
            <Button variant="outline" size="sm" onClick={() => goto('settings')}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit
            </Button>
          }
        >
          <KV
            rows={[
              ['Client ID', <CopyId key="id" value={c.client_id} />],
              [
                'Client type',
                <span key="t">
                  <Tag>{c.client_type}</Tag>{' '}
                  <span className="text-[12.5px] text-muted-foreground">
                    {isPublic
                      ? 'Runs on the user’s device; holds no secret.'
                      : 'Runs on your server; authenticates with a secret.'}
                  </span>
                </span>,
              ],
              ['Token auth', <Mono key="a">{c.token_endpoint_auth_method}</Mono>],
              [
                'Grant types',
                <span key="g" className="flex flex-wrap gap-1">
                  {c.allowed_grant_types.map((g) => (
                    <Tag key={g} solid>
                      {g}
                    </Tag>
                  ))}
                </span>,
              ],
              [
                'PKCE',
                c.pkce_required ? (
                  <StatusPill key="p" tone="ok">Required · S256</StatusPill>
                ) : (
                  <StatusPill key="p" tone="warn">Optional</StatusPill>
                ),
              ],
              [
                'Scopes',
                <dl key="s" className="grid gap-1.5">
                  {c.allowed_scopes.map((s) => (
                    <div key={s} className="flex flex-wrap items-baseline gap-x-2.5">
                      <dt>
                        <Tag>{s}</Tag>
                      </dt>
                      <dd className="text-[12.5px] text-muted-foreground">{SCOPE_INFO[s] || ''}</dd>
                    </div>
                  ))}
                </dl>,
              ],
              [
                'Redirect URIs',
                <span key="r">
                  <span className="font-mono tabular-nums">{c.redirect_uris.length}</span> registered ·{' '}
                  <button type="button" className="text-primary hover:underline" onClick={() => goto('redirects')}>
                    Manage
                  </button>
                </span>,
              ],
              [
                'Last updated',
                <span key="u">
                  {fmtDateTime(c.updated_at)}{' '}
                  <span className="text-[12.5px] text-muted-foreground">· {ago(c.updated_at)}</span>
                </span>,
              ],
            ]}
          />
        </Panel>

        <Panel title="Quick start" sub="Pre-filled with this client’s ID, first redirect URI and scopes.">
          {codeFlow && <CodeBlock label="JavaScript · authorize" code={authorizeSnippet(c)} />}
          <CodeBlock label="Shell · token" code={tokenSnippet(c)} />
        </Panel>
      </div>

      <div className="min-w-0 space-y-5">
        <Panel
          title="Endpoints"
          sub={
            <>
              Issuer <Mono className="break-all">{API_URL}</Mono>
            </>
          }
        >
          {ENDPOINTS.map(([label, path]) => (
            <div key={label} className="space-y-1">
              <DevLabel>{label}</DevLabel>
              <CopyId value={API_URL + path} className="w-full" />
            </div>
          ))}
        </Panel>

        <Panel title="Recent activity" sub="Audit events for this client.">
          {eventsLoading ? (
            <div className="h-16 animate-pulse rounded bg-foreground/[0.05]" aria-busy="true" />
          ) : events && events.length > 0 ? (
            <ul className="-my-1 divide-y divide-border">
              {events.map((e) => (
                <li key={e.id}>
                  <Link
                    to={`/audit?event=${encodeURIComponent(e.id)}`}
                    className="flex items-center justify-between gap-3 py-2.5 hover:bg-foreground/[0.02]"
                  >
                    <span className="min-w-0">
                      <code className="block truncate font-mono text-[12.5px]" title={e.action}>
                        {e.action}
                      </code>
                      <span className="block truncate text-[12.5px] text-muted-foreground">
                        {e.ipAddress || 'Unknown IP'}
                      </span>
                    </span>
                    <span className="whitespace-nowrap font-mono text-[12px] text-muted-foreground">
                      {ago(e.timestamp)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-muted-foreground">No events recorded for this client yet.</p>
          )}
        </Panel>
      </div>
    </div>
  );
}

/* ── redirect URIs ──────────────────────────────────────── */

function RedirectsPanel({ client: c }: { client: OAuthClient }) {
  const { toast } = useToast();
  const update = useUpdateClient();
  const isPublic = c.client_type === 'public';
  const codeFlow = c.allowed_grant_types.includes('authorization_code');
  const [uris, setUris] = useState<string[]>(c.redirect_uris);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const dirty = JSON.stringify(uris) !== JSON.stringify(c.redirect_uris);

  const save = async () => {
    const cleaned = uris.map((u) => u.trim()).filter(Boolean);
    if (codeFlow && cleaned.length === 0) {
      setUris(['']);
      setErrors({ 0: 'Enter a redirect URI.' });
      return;
    }
    const errs = validateUriList(cleaned, c.client_type, false);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setUris(cleaned);
      return;
    }
    try {
      await update.mutateAsync({ id: c.id, data: { redirect_uris: cleaned } });
      toast({
        title: 'Redirect URIs saved',
        description: `${cleaned.length} registered (was ${c.redirect_uris.length}).`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not save redirect URIs',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    }
  };

  return (
    <Panel
      title="Redirect URIs"
      sub={`authrz.me only redirects to an exact, registered match. ${
        isPublic
          ? 'Native apps may use a reverse-domain custom scheme or a loopback address.'
          : 'Use https; plain http is accepted for localhost only.'
      }`}
      footer={
        <>
          <span className="mr-auto text-[12.5px] text-muted-foreground" aria-live="polite">
            {dirty ? 'Unsaved changes' : `${c.redirect_uris.length} saved`}
          </span>
          <Button
            variant="outline"
            disabled={!dirty || update.isPending}
            onClick={() => {
              setUris(c.redirect_uris);
              setErrors({});
            }}
          >
            Discard
          </Button>
          <Button disabled={!dirty || update.isPending} onClick={() => void save()}>
            {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save URIs
          </Button>
        </>
      }
    >
      {!codeFlow && (
        <Callout title="Not used by this client">
          Redirect URIs only apply to the authorization code grant, which this client doesn’t allow.
        </Callout>
      )}
      {uris.length === 0 ? (
        <EmptyState icon={<Link2 />} title="No redirect URIs">
          {codeFlow
            ? 'Add at least one — authorization requests without a registered URI are rejected.'
            : 'Optional for this client.'}
        </EmptyState>
      ) : null}
      {uris.length > 0 && (
        <UriListEditor
          uris={uris}
          onChange={(u) => {
            setUris(u);
            setErrors({});
          }}
          clientType={c.client_type}
          errors={errors}
          minRows={0}
        />
      )}
      {uris.length === 0 && (
        <Button type="button" variant="outline" size="sm" onClick={() => setUris([''])}>
          Add URI
        </Button>
      )}
    </Panel>
  );
}

/* ── credentials ────────────────────────────────────────── */

function CredentialsPanel({ client: c }: { client: OAuthClient }) {
  const { toast } = useToast();
  const rotate = useRotateClientSecret();
  const [confirm, setConfirm] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);

  if (c.client_type === 'public') {
    return (
      <Panel
        title="No client secret"
        sub="Public clients run where a secret can’t be kept, so authrz.me never issues one."
      >
        <Callout tone="ok" title="PKCE protects every authorization">
          Each sign-in creates a one-time <Mono>code_verifier</Mono>; only its SHA-256{' '}
          <Mono>code_challenge</Mono> goes to the authorize endpoint. A stolen code is useless
          without the verifier.
        </Callout>
        <CodeBlock label="JavaScript · PKCE pair" code={PKCE_SNIPPET} />
        <p className="text-[12.5px] text-muted-foreground">
          Need a secret? Create a separate confidential client for your server — client type can’t
          be changed after creation.
        </p>
      </Panel>
    );
  }

  const since = c.secret_rotated_at || c.created_at;

  const doRotate = async () => {
    try {
      const result = await rotate.mutateAsync(c.id);
      setConfirm(false);
      setSecret(result.client_secret);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not rotate secret',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    }
  };

  return (
    <>
      <Panel
        title="Client secret"
        sub={
          <>
            Used by your server at the token endpoint via <Mono>{c.token_endpoint_auth_method}</Mono>.
          </>
        }
        footer={
          <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => setConfirm(true)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Rotate secret
          </Button>
        }
      >
        <KV
          rows={[
            ['Client ID', <CopyId key="id" value={c.client_id} />],
            [
              'Secret',
              <span key="s" className="flex items-center gap-2">
                <code className="font-mono text-muted-foreground">azs_••••••••••••••••</code>
                <StatusPill>hashed</StatusPill>
              </span>,
            ],
            [
              'Issued',
              <span key="i">
                {fmtDateTime(since)} <span className="text-[12.5px] text-muted-foreground">· {ago(since)}</span>
              </span>,
            ],
          ]}
        />
        <Callout icon={<Lock className="h-4 w-4" />} title="Secrets can’t be viewed again">
          authrz.me stores only a hash. If the secret is lost or exposed, rotate it — the old secret
          stops working immediately.
        </Callout>
      </Panel>

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        danger
        title={`Rotate the secret for ${c.name}?`}
        body={
          <>
            The current secret stops working <b>immediately</b>. Deploy the new secret to every
            server using <Mono>{c.client_id}</Mono> right after rotating.
          </>
        }
        confirmLabel="Rotate secret"
        pendingLabel="Rotating…"
        pending={rotate.isPending}
        onConfirm={doRotate}
      />

      <SecretDialog
        open={secret !== null}
        title="Copy the new client secret"
        description={`${c.name} authenticates to the token endpoint with this secret. authrz.me stores only a hash.`}
        onDone={() => {
          setSecret(null);
          toast({ title: 'Secret rotated', description: 'The previous secret no longer works.' });
        }}
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <DevLabel>Client ID</DevLabel>
            <CopyId value={c.client_id} />
          </div>
          {secret && <SecretField label="Client secret" value={secret} />}
        </div>
      </SecretDialog>
    </>
  );
}

/* ── logout ─────────────────────────────────────────────── */

function LogoutPanel({ client: c }: { client: OAuthClient }) {
  const { toast } = useToast();
  const update = useUpdateClient();
  const [front, setFront] = useState(c.frontchannel_logout_uri || '');
  const [back, setBack] = useState(c.backchannel_logout_uri || '');
  const [errs, setErrs] = useState<{ front?: string; back?: string }>({});
  const dirty = front !== (c.frontchannel_logout_uri || '') || back !== (c.backchannel_logout_uri || '');

  const save = async () => {
    const e = { front: validateOptionalHttps(front), back: validateOptionalHttps(back) };
    setErrs(e);
    if (e.front || e.back) return;
    try {
      await update.mutateAsync({
        id: c.id,
        data: { frontchannel_logout_uri: front.trim(), backchannel_logout_uri: back.trim() },
      });
      toast({ title: 'Logout URIs saved' });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not save logout URIs',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    }
  };

  return (
    <Panel
      title="Logout"
      sub="Tell this app when a user signs out of authrz.me so its own session ends too."
      footer={
        <>
          <Button
            variant="outline"
            disabled={!dirty || update.isPending}
            onClick={() => {
              setFront(c.frontchannel_logout_uri || '');
              setBack(c.backchannel_logout_uri || '');
              setErrs({});
            }}
          >
            Discard
          </Button>
          <Button disabled={!dirty || update.isPending} onClick={() => void save()}>
            {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save logout URIs
          </Button>
        </>
      }
    >
      <div className="space-y-1.5">
        <DevLabel htmlFor="fcl">Front-channel logout URI</DevLabel>
        <Input
          id="fcl"
          className="font-mono"
          value={front}
          onChange={(e) => setFront(e.target.value)}
          onBlur={() => setErrs((x) => ({ ...x, front: validateOptionalHttps(front) }))}
          placeholder="https://app.example.com/oidc/frontchannel-logout"
          spellCheck={false}
          aria-invalid={errs.front ? true : undefined}
        />
        <Hint>Loaded in a hidden iframe on the authrz.me sign-out page. Works for browser apps.</Hint>
        <InlineError>{errs.front}</InlineError>
      </div>
      <div className="space-y-1.5">
        <DevLabel htmlFor="bcl">Back-channel logout URI</DevLabel>
        <Input
          id="bcl"
          className="font-mono"
          value={back}
          onChange={(e) => setBack(e.target.value)}
          onBlur={() => setErrs((x) => ({ ...x, back: validateOptionalHttps(back) }))}
          placeholder="https://app.example.com/oidc/backchannel-logout"
          spellCheck={false}
          aria-invalid={errs.back ? true : undefined}
        />
        <Hint>
          authrz.me POSTs a signed <Mono>logout_token</Mono> here, server to server.
        </Hint>
        <InlineError>{errs.back}</InlineError>
      </div>
      <div className="space-y-1.5">
        <DevLabel>End-session endpoint</DevLabel>
        <CopyId value={`${API_URL}/oauth/logout`} className="w-full" />
        <Hint>
          Send users here with <Mono>id_token_hint</Mono>, <Mono>post_logout_redirect_uri</Mono> and{' '}
          <Mono>state</Mono> to sign out everywhere.
        </Hint>
      </div>
    </Panel>
  );
}

/* ── settings ───────────────────────────────────────────── */

function SettingsPanel({
  client: c,
  toggling,
  onToggle,
  onDeleted,
}: {
  client: OAuthClient;
  toggling: boolean;
  onToggle: () => void;
  onDeleted: (name: string) => void;
}) {
  const { toast } = useToast();
  const update = useUpdateClient();
  const del = useDeleteClient();
  const isPublic = c.client_type === 'public';

  const [name, setName] = useState(c.name);
  const [auth, setAuth] = useState<TokenEndpointAuthMethod>(c.token_endpoint_auth_method);
  const [pkce, setPkce] = useState(c.pkce_required);
  const [grants, setGrants] = useState<GrantType[]>(c.allowed_grant_types);
  const [scopes, setScopes] = useState<string[]>(c.allowed_scopes);
  const [errs, setErrs] = useState<{ name?: string; grants?: string; scopes?: string }>({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  const dirty = useMemo(
    () =>
      name !== c.name ||
      auth !== c.token_endpoint_auth_method ||
      pkce !== c.pkce_required ||
      [...grants].sort().join() !== [...c.allowed_grant_types].sort().join() ||
      [...scopes].sort().join() !== [...c.allowed_scopes].sort().join(),
    [name, auth, pkce, grants, scopes, c]
  );

  const save = async () => {
    const e = {
      name: name.trim() ? '' : 'Enter a name.',
      grants: grants.length ? '' : 'Choose at least one grant type.',
      scopes: scopes.length ? '' : 'Choose at least one scope.',
    };
    setErrs(e);
    if (e.name || e.grants || e.scopes) return;
    try {
      await update.mutateAsync({
        id: c.id,
        data: {
          name: name.trim(),
          allowed_grant_types: grants,
          allowed_scopes: scopes,
          ...(isPublic ? {} : { token_endpoint_auth_method: auth, pkce_required: pkce }),
        },
      });
      toast({ title: 'Settings saved', description: name.trim() });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not save settings',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    }
  };

  const doDelete = async () => {
    try {
      await del.mutateAsync(c.id);
      setConfirmDelete(false);
      onDeleted(c.name);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not delete client',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    }
  };

  return (
    <div className="space-y-5">
      <Panel
        title="Client settings"
        sub="Changes apply to the next authorization request."
        footer={
          <>
            <Button
              variant="outline"
              disabled={!dirty || update.isPending}
              onClick={() => {
                setName(c.name);
                setAuth(c.token_endpoint_auth_method);
                setPkce(c.pkce_required);
                setGrants(c.allowed_grant_types);
                setScopes(c.allowed_scopes);
                setErrs({});
              }}
            >
              Discard
            </Button>
            <Button disabled={!dirty || update.isPending} onClick={() => void save()}>
              {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save settings
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <DevLabel htmlFor="sName">Name (required)</DevLabel>
            <Input
              id="sName"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setErrs((x) => ({ ...x, name: name.trim() ? '' : 'Enter a name.' }))}
              aria-invalid={errs.name ? true : undefined}
            />
            <Hint>Shown to users on the consent screen.</Hint>
            <InlineError>{errs.name}</InlineError>
          </div>
          <div className="space-y-1.5">
            <DevLabel htmlFor="sApp">App</DevLabel>
            <Input id="sApp" value={c.product_name || c.product_id} disabled />
            <Hint>Clients can’t move between apps.</Hint>
          </div>
          <div className="space-y-1.5">
            <DevLabel htmlFor="sType">Client type</DevLabel>
            <Input id="sType" value={isPublic ? 'Public' : 'Confidential'} disabled />
            <Hint>Fixed at creation.</Hint>
          </div>
          <div className="space-y-1.5">
            <DevLabel htmlFor="sAuth">Token endpoint auth</DevLabel>
            <Select
              value={isPublic ? 'none' : auth}
              onValueChange={(v: TokenEndpointAuthMethod) => setAuth(v)}
              disabled={isPublic}
            >
              <SelectTrigger id="sAuth">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(isPublic ? ['none'] : ['client_secret_basic', 'client_secret_post']).map((m) => (
                  <SelectItem key={m} value={m}>
                    {AUTH_METHODS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Hint>{isPublic ? 'Public clients don’t authenticate.' : 'How your server sends the secret.'}</Hint>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3.5 py-3">
          <div>
            <b className="text-[13.5px] font-semibold">Require PKCE</b>
            <p className="text-[12.5px] text-muted-foreground">
              {isPublic
                ? 'Always on for public clients.'
                : 'Recommended even with a secret — it stops code injection.'}
            </p>
          </div>
          <Switch
            checked={isPublic ? true : pkce}
            onCheckedChange={setPkce}
            disabled={isPublic}
            aria-label="Require PKCE"
          />
        </div>

        <div className="space-y-1.5">
          <DevLabel>Grant types</DevLabel>
          <GrantPicker
            value={grants}
            clientType={c.client_type}
            onChange={(v) => {
              setGrants(v);
              setErrs((x) => ({ ...x, grants: '' }));
            }}
          />
          <InlineError>{errs.grants}</InlineError>
        </div>
        <div className="space-y-1.5">
          <DevLabel>Allowed scopes</DevLabel>
          <ScopePicker
            value={scopes}
            onChange={(v) => {
              setScopes(v);
              setErrs((x) => ({ ...x, scopes: '' }));
            }}
          />
          <InlineError>{errs.scopes}</InlineError>
        </div>
      </Panel>

      <Panel
        title="Danger zone"
        sub="These actions affect every user signing in through this client."
        className="border-destructive/30"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <b className="text-[13.5px] font-semibold">{c.is_active ? 'Disable client' : 'Enable client'}</b>
            <p className="text-[12.5px] text-muted-foreground">
              {c.is_active
                ? 'Reject new sign-ins and refresh tokens. Reversible.'
                : 'Allow sign-ins and token requests again.'}
            </p>
          </div>
          <Button
            variant="outline"
            className={c.is_active ? 'text-destructive hover:text-destructive' : ''}
            disabled={toggling}
            onClick={onToggle}
          >
            {c.is_active ? <Pause className="mr-2 h-4 w-4" /> : <Power className="mr-2 h-4 w-4" />}
            {c.is_active ? 'Disable' : 'Enable'}
          </Button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <div>
            <b className="text-[13.5px] font-semibold">Delete client</b>
            <p className="text-[12.5px] text-muted-foreground">
              Permanently remove it and revoke every token it issued.
            </p>
          </div>
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </Panel>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        danger
        title={`Delete ${c.name}?`}
        body={
          <>
            This permanently deletes the client and revokes every token it issued. Apps using{' '}
            <Mono>{c.client_id}</Mono> stop working at once.
          </>
        }
        confirmLabel="Delete client"
        pendingLabel="Deleting…"
        typeToConfirm={c.name}
        pending={del.isPending}
        onConfirm={doDelete}
      />
    </div>
  );
}
